import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import AppLayout from "../layout/AppLayout"
import { getAdminUsers, updateUserStatus, updateUserRole, updateUserFilingApproval, resetUserTwoFactor, AdminUser, AdminUsersResponse } from "../services/userService"
import { RootState } from "../store/store"
import ConfirmDialog from "../components/ui/ConfirmDialog"
import { FiLoader, FiSearch, FiUsers } from "react-icons/fi"

const PAGE_SIZE = 20

function Avatar({ firstName, lastName, isAdmin }: { firstName: string; lastName: string; isAdmin: boolean }) {
  const initials = `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase()
  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${
        isAdmin ? "bg-dantzBlue" : "bg-secondary4"
      }`}
    >
      {initials}
    </div>
  )
}

function UserRow({
  user,
  isSelf,
  onToggleStatus,
  onToggleRole,
  onToggleApproval,
  onResetTwoFactor,
}: {
  user: AdminUser
  isSelf: boolean
  onToggleStatus: (user: AdminUser) => void
  onToggleRole: (user: AdminUser) => void
  onToggleApproval: (user: AdminUser) => void
  onResetTwoFactor: (user: AdminUser) => void
}) {
  return (
    <tr
      className="border-l-[3px] transition-colors hover:bg-dantzGray/40"
      style={{ borderLeftColor: user.isAdmin ? "#F68B1F" : "transparent" }}
    >
      <td className="whitespace-nowrap px-5 py-4">
        <div className="flex items-center gap-2.5">
          <Avatar firstName={user.firstName} lastName={user.lastName} isAdmin={user.isAdmin} />
          <span className="text-sm font-medium text-dantzBlue">
            {user.firstName} {user.lastName}
          </span>
        </div>
      </td>
      <td className="whitespace-nowrap px-5 py-4 text-sm text-secondary4">{user.email}</td>
      <td className="px-5 py-4 text-sm text-secondary4">
        {user.company || <span className="text-secondary5">—</span>}
      </td>
      <td className="whitespace-nowrap px-5 py-4">
        <div className="flex flex-col items-start gap-1">
          <span className={`text-sm ${user.active ? "text-dantzBlue" : "text-secondary4"}`}>
            {user.active ? "Aktiv" : "Inaktiv"}
          </span>
          {!isSelf && (
            <button
              onClick={() => onToggleStatus(user)}
              className={`text-xs font-medium transition hover:underline ${
                user.active ? "text-dantzOrange" : "text-dantzBlue"
              }`}
            >
              {user.active ? "Deaktivieren" : "Aktivieren"}
            </button>
          )}
        </div>
      </td>
      <td className="whitespace-nowrap px-5 py-4">
        <div className="flex flex-col items-start gap-1">
          <span className={`text-sm ${user.isAdmin ? "font-medium text-dantzOrange" : "text-secondary4"}`}>
            {user.isAdmin ? "Admin" : "User"}
          </span>
          {!isSelf && (
            <button
              onClick={() => onToggleRole(user)}
              className={`text-xs font-medium transition hover:underline ${
                user.isAdmin ? "text-dantzOrange" : "text-dantzBlue"
              }`}
            >
              {user.isAdmin ? "Admin entfernen" : "Admin machen"}
            </button>
          )}
          <button
            onClick={() => onToggleApproval(user)}
            className={`text-xs font-medium transition hover:underline ${
              user.canReleaseFilings ? "text-dantzOrange" : "text-dantzBlue"
            }`}
          >
            {user.canReleaseFilings ? "Einreichen entziehen" : "Einreichen erlauben"}
          </button>
          {user.twoFactorEnabled && !isSelf && (
            <button
              onClick={() => onResetTwoFactor(user)}
              className="text-xs font-medium text-dantzOrange transition hover:underline"
            >
              2FA zurücksetzen
            </button>
          )}
        </div>
      </td>
      <td className="px-5 py-4 text-sm">
        <span className={user.verified ? "text-dantzBlue" : "text-secondary4"}>
          {user.verified ? "Ja" : "Nein"}
        </span>
      </td>
      <td className="whitespace-nowrap px-5 py-4 text-sm tabular-nums text-secondary4">
        {new Date(user.createdAt).toLocaleDateString("de-DE")}
      </td>
    </tr>
  )
}

export default function UsersPage() {
  const currentUser = useSelector((state: RootState) => state.auth.user)
  const [data, setData] = useState<AdminUsersResponse | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [statusTarget, setStatusTarget] = useState<AdminUser | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [roleTarget, setRoleTarget] = useState<AdminUser | null>(null)
  const [roleError, setRoleError] = useState<string | null>(null)
  const [approvalTarget, setApprovalTarget] = useState<AdminUser | null>(null)
  const [twoFactorTarget, setTwoFactorTarget] = useState<AdminUser | null>(null)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1)
      setSearch(searchInput.trim())
    }, 400)
    return () => clearTimeout(timeout)
  }, [searchInput])

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      try {
        const result = await getAdminUsers(page, PAGE_SIZE, search)
        if (!cancelled) setData(result)
      } catch {
        if (!cancelled) setError("Die Benutzerliste konnte nicht geladen werden.")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [page, search])

  const totalPages = data?.totalPages ?? 1

  async function handleConfirmToggle() {
    if (!statusTarget) return

    const nextActive = !statusTarget.active
    try {
      await updateUserStatus(statusTarget._id, nextActive)
      setData((prev) =>
        prev
          ? {
              ...prev,
              users: prev.users.map((user) =>
                user._id === statusTarget._id ? { ...user, active: nextActive } : user
              ),
            }
          : prev
      )
      setStatusTarget(null)
      setStatusError(null)
    } catch {
      setStatusError("Status konnte nicht geändert werden.")
      setStatusTarget(null)
    }
  }

  async function handleConfirmRoleToggle() {
    if (!roleTarget) return

    const nextIsAdmin = !roleTarget.isAdmin
    try {
      await updateUserRole(roleTarget._id, nextIsAdmin)
      setData((prev) =>
        prev
          ? {
              ...prev,
              users: prev.users.map((user) =>
                user._id === roleTarget._id ? { ...user, isAdmin: nextIsAdmin } : user
              ),
            }
          : prev
      )
      setRoleTarget(null)
      setRoleError(null)
    } catch {
      setRoleError("Rolle konnte nicht geändert werden.")
      setRoleTarget(null)
    }
  }

  async function handleConfirmApprovalToggle() {
    if (!approvalTarget) return

    const next = !approvalTarget.canReleaseFilings
    try {
      await updateUserFilingApproval(approvalTarget._id, next)
      setData((prev) =>
        prev
          ? {
              ...prev,
              users: prev.users.map((user) =>
                user._id === approvalTarget._id ? { ...user, canReleaseFilings: next } : user
              ),
            }
          : prev
      )
      setApprovalTarget(null)
      setRoleError(null)
    } catch {
      setRoleError("Recht konnte nicht geändert werden.")
      setApprovalTarget(null)
    }
  }

  async function handleConfirmTwoFactorReset() {
    if (!twoFactorTarget) return

    try {
      await resetUserTwoFactor(twoFactorTarget._id)
      setData((prev) =>
        prev
          ? {
              ...prev,
              users: prev.users.map((user) =>
                user._id === twoFactorTarget._id ? { ...user, twoFactorEnabled: false } : user
              ),
            }
          : prev
      )
      setTwoFactorTarget(null)
      setRoleError(null)
    } catch {
      setRoleError("Zwei-Faktor-Anmeldung konnte nicht zurückgesetzt werden.")
      setTwoFactorTarget(null)
    }
  }

  return (
    <AppLayout title="Users" wide>
      <div className="rounded-xl border border-secondary5 bg-white overflow-hidden">
        <div className="border-b border-secondary5 px-6 py-4">
          <div className="relative w-full max-w-sm">
            <FiSearch
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-secondary4"
            />
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Suche nach Name, E-Mail oder Unternehmen..."
              className="w-full rounded-lg border border-secondary5 py-2 pl-9 pr-3 text-sm focus:border-dantzOrange focus:outline-none focus:ring-2 focus:ring-dantzOrange/20"
            />
          </div>
        </div>

        {statusError && (
          <div className="border-b border-secondary5 bg-red-50 px-4 py-2 text-sm text-red-600">
            {statusError}
          </div>
        )}

        {roleError && (
          <div className="border-b border-secondary5 bg-red-50 px-4 py-2 text-sm text-red-600">
            {roleError}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center gap-2 py-20 text-secondary4">
            <FiLoader size={18} className="animate-spin" />
            <span className="text-sm">Laden...</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center py-20 text-center">
            <p className="text-sm text-red-500">{error}</p>
            <button
              onClick={() => setPage(1)}
              className="mt-3 text-sm text-dantzBlue underline"
            >
              Erneut versuchen
            </button>
          </div>
        )}

        {!loading && !error && data?.users.length === 0 && (
          <div className="flex flex-col items-center py-20 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-dantzGray text-dantzBlue">
              <FiUsers size={24} />
            </div>
            <p className="mt-4 text-sm text-secondary4">Keine Benutzer gefunden.</p>
          </div>
        )}

        {!loading && !error && data && data.users.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-dantzBlue">
                  <tr>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white/80">Name</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white/80">E-Mail</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white/80">Unternehmen</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white/80">Status</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white/80">Rolle</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white/80">Verifiziert</th>
                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white/80">Erstellt am</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary5">
                  {data.users.map((user) => (
                    <UserRow
                      key={user._id}
                      user={user}
                      isSelf={user._id === currentUser?.id}
                      onToggleStatus={setStatusTarget}
                      onToggleRole={setRoleTarget}
                      onToggleApproval={setApprovalTarget}
                      onResetTwoFactor={setTwoFactorTarget}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-secondary5 px-6 py-4">
              <p className="text-xs text-secondary4">
                {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, data.total)} von {data.total} Benutzern
              </p>
              <div className="flex items-center gap-1">
                <button
                  className="rounded-md px-3 py-1.5 text-sm text-secondary4 transition hover:bg-dantzGray disabled:opacity-40"
                  onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                  disabled={page === 1}
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index}
                    className={`rounded-md px-3 py-1.5 text-sm transition ${page === index + 1 ? "bg-dantzBlue text-white" : "text-secondary4 hover:bg-dantzGray"}`}
                    onClick={() => setPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  className="rounded-md px-3 py-1.5 text-sm text-secondary4 transition hover:bg-dantzGray disabled:opacity-40"
                  onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {statusTarget && (
        <ConfirmDialog
          message={
            statusTarget.active
              ? `${statusTarget.firstName} ${statusTarget.lastName} deaktivieren?`
              : `${statusTarget.firstName} ${statusTarget.lastName} aktivieren?`
          }
          confirmLabel={statusTarget.active ? "Deaktivieren" : "Aktivieren"}
          variant={statusTarget.active ? "danger" : "primary"}
          onCancel={() => setStatusTarget(null)}
          onConfirm={handleConfirmToggle}
        />
      )}

      {approvalTarget && (
        <ConfirmDialog
          message={
            approvalTarget.canReleaseFilings
              ? `${approvalTarget.firstName} ${approvalTarget.lastName} das Recht zum Einreichen entziehen?`
              : `${approvalTarget.firstName} ${approvalTarget.lastName} erlauben, geprüfte Anmeldungen einzureichen?`
          }
          confirmLabel={approvalTarget.canReleaseFilings ? "Entziehen" : "Erlauben"}
          variant={approvalTarget.canReleaseFilings ? "danger" : "primary"}
          onCancel={() => setApprovalTarget(null)}
          onConfirm={handleConfirmApprovalToggle}
        />
      )}

      {twoFactorTarget && (
        <ConfirmDialog
          message={`${twoFactorTarget.firstName} ${twoFactorTarget.lastName}: Zwei-Faktor-Anmeldung zurücksetzen? Die Person wird abgemeldet und kann sie neu einrichten.`}
          confirmLabel="Zurücksetzen"
          variant="danger"
          onCancel={() => setTwoFactorTarget(null)}
          onConfirm={handleConfirmTwoFactorReset}
        />
      )}

      {roleTarget && (
        <ConfirmDialog
          message={
            roleTarget.isAdmin
              ? `${roleTarget.firstName} ${roleTarget.lastName} die Admin-Rechte entziehen?`
              : `${roleTarget.firstName} ${roleTarget.lastName} zum Admin machen?`
          }
          confirmLabel={roleTarget.isAdmin ? "Admin entfernen" : "Admin machen"}
          variant={roleTarget.isAdmin ? "danger" : "primary"}
          onCancel={() => setRoleTarget(null)}
          onConfirm={handleConfirmRoleToggle}
        />
      )}
    </AppLayout>
  )
}
